import React from 'react';
import { useNavigate } from 'react-router-dom';
import { McButton } from '@maersk-global/mds-react-wrapper/components-core/mc-button';
import { McCard } from '@maersk-global/mds-react-wrapper/components-core/mc-card';
import { McIcon } from '@maersk-global/mds-react-wrapper/components-core/mc-icon';
import { McTag } from '@maersk-global/mds-react-wrapper/components-core/mc-tag';
import { McFooter } from '@maersk-global/mds-react-wrapper/components-core/mc-footer';
import Header from '../Header';

// Built only from the Maersk Design System: MDS fonts, design tokens, foundations
// (typography, grid, gap, table) and mc-* components. No custom colours or fonts.

const DOCS_URL = 'https://docs.maersk.io/neurix';
const NEURIX_URL = 'https://neurix.maersk.io/';
const TRY_DEMO_URL = 'https://neurix.maersk.io/try-demo/own-document';
const ONBOARDING_URL = 'https://teams.microsoft.com/l/channel/19%3Aa9e7c464f7334664a4b25f537f2f7cb4%40thread.tacv2/%F0%9F%9A%80%20Onboarding?groupId=34b4cb7b-75d7-4459-b006-e36c4dcc8f2a&tenantId=05d75c05-fa1a-42e7-9cf1-eb416c396f2d';

const STEPS = [
  {
    icon: 'file-pdf',
    title: 'Upload a document',
    description: 'PDFs, scans, spreadsheets or images. Text and layout are extracted for you.',
  },
  {
    icon: 'pencil',
    title: 'Choose an instruction',
    description: 'Pick a saved prompt and response format, or write a new one in Instructions.',
  },
  {
    icon: 'file-check',
    title: 'Parse and review',
    description: 'Compare models, check every field, and copy clean JSON for your workflow.',
  },
];

const DESTINATIONS = [
  {
    id: 'docs',
    heading: 'Docs',
    body: 'Guides, tutorials and best practices for building on Neurix.',
    href: DOCS_URL,
  },
  {
    id: 'neurix',
    heading: 'Neurix',
    body: 'Run your instructions in production and manage your workflows.',
    href: NEURIX_URL,
  },
  {
    id: 'onboarding',
    heading: 'Request Onboarding',
    body: 'Ask the Neurix team to onboard your use case in the Onboarding channel on Teams.',
    href: ONBOARDING_URL,
  },
];

const SAMPLE_FIELDS = [
  ['Shipper', 'Maersk A/S'],
  ['Port of loading', 'Aarhus, DK'],
  ['Port of discharge', 'Singapore'],
  ['Containers', '3'],
];

const token = (name) => `var(--mds_brand_appearance_${name})`;

const section = {
  maxWidth: 1200,
  margin: '0 auto',
  padding: '0 24px',
  boxSizing: 'border-box',
  width: '100%',
};

// Stylised preview of the Playground flow: document → instruction → extracted fields.
const FlowPreview = () => (
  <div aria-hidden="true" className="mds-flex mds-flex-column mds-gap-300" style={{ maxWidth: 440, width: '100%', justifySelf: 'center' }}>
    <McCard variant="bordered" fit="small" heading="bill_of_lading_0423.pdf" subheading="3 pages · layout extracted" body="" />

    <div className="mds-flex mds-flex-items-center mds-gap-200" style={{ paddingLeft: 16 }}>
      <McIcon icon="arrow-down" />
      <McTag appearance="info" fit="small" label="Instruction · BL Parser" />
    </div>

    <div style={{
      backgroundColor: token('neutral_default_background-color'),
      border: `1px solid ${token('neutral_default_border-color')}`,
      borderRadius: 'var(--mds_brand_border_large_radius)',
      padding: 16,
    }}>
      <p className="mds-text--small-bold" style={{ margin: '0 0 8px' }}>Extracted fields</p>
      <div className="mds-table mds-table--small mds-table--header-none mds-table--disable-row-highlight-on-hover">
        <table>
          <tbody>
            {SAMPLE_FIELDS.map(([field, value]) => (
              <tr key={field}>
                <td style={{ color: token('neutral_weak_text-color') }}>{field}</td>
                <td>{value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </div>
);

const HomePage = () => {
  const navigate = useNavigate();

  return (
    <div className="mds" style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: token('neutral_default_background-color'),
      color: token('neutral_default_text-color'),
    }}>
      <Header />

      {/* Hero */}
      <section style={{ backgroundColor: token('neutral_weakest_background-color'), padding: '64px 0 72px' }}>
        <div className="mds-container" style={section}>
          <div className="mds-grid">
            <div className="mds-grid-md-col-span-6 mds-grid-sm-col-span-6 mds-grid-xs-col-span-2 mds-grid-align-center">
              <McTag appearance="neutral-default" fit="small" label="Beta" />
              <h1 className="mds-headline--large" style={{ margin: '16px 0' }}>
                Turn documents into structured data
              </h1>
              <p className="mds-text--large-normal" style={{ margin: '0 0 32px', color: token('neutral_weak_text-color'), maxWidth: 520 }}>
                Upload a document, pick an instruction, and get clean JSON you can review, refine and
                take straight to Neurix.
              </p>
              <div className="mds-flex mds-gap-300" style={{ flexWrap: 'wrap' }}>
                <McButton
                  data-testid="home-card-playground"
                  label="Open Playground"
                  appearance="primary"
                  variant="filled"
                  fit="medium"
                  trailingicon="arrow-right"
                  click={() => navigate('/playground')}
                />
                <McButton
                  data-testid="home-link-try-demo"
                  label="Try Demo"
                  appearance="neutral"
                  variant="outlined"
                  fit="medium"
                  trailingicon="square-arrow-up-right"
                  href={TRY_DEMO_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                />
                <McButton
                  data-testid="home-link-instructions"
                  label="Browse instructions"
                  appearance="neutral"
                  variant="outlined"
                  fit="medium"
                  click={() => navigate('/system-messages')}
                />
              </div>
              <p className="mds-text--small-normal" style={{ margin: '24px 0 0', color: token('neutral_weak_text-color') }}>
                Neurix Lab is in beta and will be integrated into the main Neurix platform.
              </p>
            </div>
            <div className="mds-grid-md-col-span-6 mds-grid-sm-col-span-6 mds-grid-xs-col-span-2">
              <FlowPreview />
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section style={{ padding: '64px 0 32px' }}>
        <div className="mds-container" style={section}>
          <h2 className="mds-headline--medium" style={{ margin: '0 0 32px' }}>Three steps from file to structured data</h2>
          <ol className="mds-grid" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {STEPS.map((step, i) => (
              <li key={step.title} className="mds-grid-md-col-span-4 mds-grid-sm-col-span-6 mds-grid-xs-col-span-2">
                <div className="mds-flex mds-flex-items-center mds-gap-300" style={{ marginBottom: 12 }}>
                  <span style={{
                    width: 40, height: 40, borderRadius: '50%',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    backgroundColor: token('secondary_weak_background-color'),
                  }}>
                    <McIcon icon={step.icon} size="24" />
                  </span>
                  <span className="mds-text--small-bold" style={{ color: token('neutral_weak_text-color') }}>Step {i + 1}</span>
                </div>
                <h3 className="mds-headline--x-small" style={{ margin: '0 0 8px' }}>{step.title}</h3>
                <p className="mds-text--medium-normal" style={{ margin: 0, color: token('neutral_weak_text-color') }}>{step.description}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Destinations */}
      <section style={{ padding: '32px 0 72px' }}>
        <div className="mds-container" style={section}>
          <div className="mds-grid">
            {DESTINATIONS.map((dest) => (
              <div key={dest.id} className="mds-grid-md-col-span-4 mds-grid-sm-col-span-6 mds-grid-xs-col-span-2">
                <McCard
                  data-testid={`home-card-${dest.id}`}
                  variant="bordered"
                  heading={dest.heading}
                  body={dest.body}
                  footer="Opens in a new tab"
                  href={dest.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ height: '100%' }}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      <div style={{ marginTop: 'auto' }}>
        <McFooter>© 2026 Maersk Group. All rights reserved. | Neurix Lab</McFooter>
      </div>
    </div>
  );
};

export default HomePage;
